<script setup>
import { ref, computed, watch, onMounted, onUnmounted, defineProps, defineEmits } from 'vue';
import { GridLayout, GridItem } from 'vue3-grid-layout';
import { PlusIcon, PencilSquareIcon } from '@heroicons/vue/24/solid';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '@/firebase';
import { useStore } from 'vuex';
import Event from '@/api/event';
import LoadingAnimation3 from '@/components/loadingIcons/LoadingAnimation3.vue';
import WidgetRenderer from './WidgetRenderer.vue';
import { hasPermission } from '@/tools/PermissionChecker';

const store = useStore();

const canEditDashboard = computed(() => hasPermission('editDashboard'));

const props = defineProps({
  dataID: {
    type: String,
    required: true
  },
  page: {
    type: Object,
    required: true
  },
  templateId: {
    type: String,
    required: false,
    default: ''
  },
  timeFormat: {
    type: String,
    default: '24hour'
  },
  streamData: {
    type: Object,
    default: () => ({})
  },
  historicalData: {
    type: Object,
    default: () => ({})
  },
  sourceErrors: {
    type: Object,
    default: () => ({})
  },
  dashboardMode: {
    type: String,
    default: ''
  },
  showEditModeToggle: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(['layoutUpdated', 'addWidget', 'editWidget', 'deleteWidget']);

// State
const editMode = ref(false);
const layout = ref(props.page.layout || []);
const widgets = ref(props.page.widgets || {});
const isSaving = ref(false);
const isMobile = ref(false);
const autoScrollInterval = ref(null);
const isInteracting = ref(false); // Track if user is dragging/resizing
const gridRootRef = ref(null);
const dragStartLayout = ref(null);
const activeDragItemId = ref(null);
const interactionType = ref(null); // 'drag' | 'resize' | null

// Computed
const hasWidgets = computed(() => layout.value.length > 0);

// Responsive layout - check if mobile
const checkMobile = () => {
  isMobile.value = window.innerWidth < 768;
};

// Mobile layout - stack widgets vertically (removed computed, replaced with function)
// const mobileLayout = computed(() => { ... })  // REMOVED

// Grid columns - 1 for mobile, 12 for desktop
const colNum = computed(() => isMobile.value ? 1 : 12);
const isParentControlledMode = computed(() => props.dashboardMode === 'view' || props.dashboardMode === 'edit');

// Shallow watch on layout reference - fires when parent replaces the page/layout object
watch(() => props.page.layout, (newLayout) => {
  layout.value = newLayout || [];
});

// Shallow watch on widgets reference - fires when parent replaces props.page.widgets
// (e.g. after saving a new widget). This clears _loading without needing deep tracking.
watch(() => props.page.widgets, (newWidgets) => {
  widgets.value = newWidgets || {};
});

watch(() => props.dashboardMode, (mode) => {
  if (!isParentControlledMode.value) return;
  editMode.value = mode === 'edit';
}, { immediate: true });

// Auto-scroll functionality for drag/resize near viewport edges
let currentMousePosition = { x: 0, y: 0 };

/** Get the element that actually scrolls (Layout's main with overflow-y-auto) */
const getScrollableElement = () => {
  const el = gridRootRef.value;
  if (!el) return null;
  return el.closest('main') || document.scrollingElement || document.documentElement;
};

const performAutoScroll = () => {
  const mouseY = currentMousePosition.y;
  const mouseX = currentMousePosition.x;
  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;
  const scrollThreshold = 120;
  const maxScrollSpeed = 25;
  
  let scrollX = 0;
  let scrollY = 0;
  
  if (mouseY < scrollThreshold) {
    const intensity = 1 - (mouseY / scrollThreshold);
    scrollY = -maxScrollSpeed * intensity;
  } else if (mouseY > viewportHeight - scrollThreshold) {
    const intensity = 1 - ((viewportHeight - mouseY) / scrollThreshold);
    scrollY = maxScrollSpeed * intensity;
  }
  
  if (mouseX < scrollThreshold) {
    const intensity = 1 - (mouseX / scrollThreshold);
    scrollX = -maxScrollSpeed * intensity;
  } else if (mouseX > viewportWidth - scrollThreshold) {
    const intensity = 1 - ((viewportWidth - mouseX) / scrollThreshold);
    scrollX = maxScrollSpeed * intensity;
  }
  
  if (scrollX === 0 && scrollY === 0) return;
  
  const scrollable = getScrollableElement();
  if (scrollable) {
    scrollable.scrollTop += scrollY;
    scrollable.scrollLeft += scrollX;
  } else {
    window.scrollBy(scrollX, scrollY);
  }
};

const startAutoScroll = () => {
  if (autoScrollInterval.value) return; // Already running
  
  // Start the auto-scroll loop at ~60fps
  autoScrollInterval.value = setInterval(performAutoScroll, 16);
};

const stopAutoScroll = () => {
  if (autoScrollInterval.value) {
    clearInterval(autoScrollInterval.value);
    autoScrollInterval.value = null;
  }
};

const handleMouseMove = (event) => {
  // Always update current mouse position
  currentMousePosition = { x: event.clientX, y: event.clientY };
  
  if (!editMode.value || isMobile.value || !isInteracting.value) return;
  
  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;
  const scrollThreshold = 100;
  
  // Check if near any edge
  const nearEdge = event.clientY < scrollThreshold || 
                   event.clientY > viewportHeight - scrollThreshold ||
                   event.clientX < scrollThreshold || 
                   event.clientX > viewportWidth - scrollThreshold;
  
  if (nearEdge && !autoScrollInterval.value) {
    startAutoScroll();
  } else if (!nearEdge && autoScrollInterval.value) {
    stopAutoScroll();
  }
};

const rectanglesOverlap = (a, b) => {
  return a.x < b.x + b.w &&
    a.x + a.w > b.x &&
    a.y < b.y + b.h &&
    a.y + a.h > b.y;
};

const canPlaceAtPosition = (candidate, layoutItems, excludeId = null) => {
  if (candidate.x < 0 || candidate.y < 0) return false;
  if (candidate.x + candidate.w > colNum.value) return false;

  return !layoutItems.some((item) => {
    if (item.i === excludeId) return false;
    return rectanglesOverlap(candidate, item);
  });
};

const captureDragStartLayout = (itemId) => {
  if (!dragStartLayout.value) {
    dragStartLayout.value = layout.value.map((item) => ({ ...item }));
  }
  if (itemId != null) {
    activeDragItemId.value = itemId;
  }
};

const clearDragTracking = () => {
  dragStartLayout.value = null;
  activeDragItemId.value = null;
};

const snapDisplacedWidgetsBack = (newLayout) => {
  if (!dragStartLayout.value || !activeDragItemId.value) {
    return newLayout;
  }

  const nextLayout = newLayout.map((item) => ({ ...item }));
  const orderedStartItems = [...dragStartLayout.value].sort((a, b) => {
    if (a.y !== b.y) return a.y - b.y;
    return a.x - b.x;
  });

  for (const startItem of orderedStartItems) {
    if (startItem.i === activeDragItemId.value) continue;

    const currentItem = nextLayout.find((item) => item.i === startItem.i);
    if (!currentItem) continue;

    const desiredPosition = {
      ...currentItem,
      x: startItem.x,
      y: startItem.y
    };

    if (canPlaceAtPosition(desiredPosition, nextLayout, currentItem.i)) {
      currentItem.x = startItem.x;
      currentItem.y = startItem.y;
    }
  }

  return nextLayout;
};

// Handle grid item interactions
const handleItemMove = (itemId) => {
  interactionType.value = 'drag';
  captureDragStartLayout(itemId);
  isInteracting.value = true;
};

const handleItemMoved = () => {
  isInteracting.value = false;
  stopAutoScroll();
};

const handleItemResize = () => {
  interactionType.value = 'resize';
  clearDragTracking();
  isInteracting.value = true;
};

const handleItemResized = () => {
  interactionType.value = null;
  clearDragTracking();
  isInteracting.value = false;
  stopAutoScroll();
};

const handleMouseUp = () => {
  if (interactionType.value !== 'drag') {
    clearDragTracking();
  }
  isInteracting.value = false;
  stopAutoScroll();
};

// Lifecycle - set up resize listener
onMounted(() => {
  checkMobile();
  window.addEventListener('resize', checkMobile);
  window.addEventListener('mousemove', handleMouseMove);
  window.addEventListener('mouseup', handleMouseUp);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile);
  window.removeEventListener('mousemove', handleMouseMove);
  window.removeEventListener('mouseup', handleMouseUp);
  stopAutoScroll();
});

// Methods
const saveLayout = async () => {
  if (!props.templateId) {
    console.error('Cannot save layout: templateId is not available');
    return;
  }
  
  isSaving.value = true;
  try {
    // Find the current page in the template and update it
    const templateRef = doc(db, 'templates', props.templateId);
    const templateDoc = await getDoc(templateRef);
    
    if (templateDoc.exists()) {
      const templateData = templateDoc.data();

      // Strip transient UI-only flags before persisting
      const cleanWidgets = {};
      for (const [id, w] of Object.entries(widgets.value)) {
        // eslint-disable-next-line no-unused-vars
        const { _loading, _eventJustCreated, ...rest } = w;
        cleanWidgets[id] = rest;
      }

      const updatedPages = templateData.pages.map(p => 
        p.id === props.page.id 
          ? { ...p, layout: layout.value, widgets: cleanWidgets }
          : p
      );
      
      await updateDoc(templateRef, { pages: updatedPages });
      
      // Update the store with the new template data
      const updatedTemplateData = {
        ...templateData,
        pages: updatedPages,
        id: props.templateId,
        dataID: props.dataID
      };
      await store.dispatch('setTemplate', updatedTemplateData);
      
      emit('layoutUpdated');
    }
  } catch (err) {
    console.error('Error saving layout:', err);
    alert('Failed to save layout. Please try again.');
  } finally {
    isSaving.value = false;
  }
};

const handleLayoutUpdate = (newLayout) => {
  // Only save layout changes from desktop view
  if (!isMobile.value) {
    layout.value = interactionType.value === 'drag'
      ? snapDisplacedWidgetsBack(newLayout)
      : newLayout;
    interactionType.value = null;
    clearDragTracking();
    if (editMode.value) {
      saveLayout();
    }
  }
};

// Get display layout - transforms for mobile or returns actual layout
const getDisplayLayout = () => {
  if (!isMobile.value) return layout.value;

  // Stack widgets tightly by accumulating each item's actual height
  let cumulativeY = 0;
  return layout.value.map((item) => {
    const mapped = { ...item, x: 0, y: cumulativeY, w: 1 };
    cumulativeY += item.h || 6;
    return mapped;
  });
};

const handleAddWidget = () => {
  editMode.value = true;
  emit('addWidget');
};

const handleEditWidget = (widgetId) => {
  emit('editWidget', widgetId);
};

const handleUpdateWidget = ({ widgetId, widget: updatedWidget }) => {
  if (widgetId == null || !(widgetId in widgets.value)) return;
  widgets.value[widgetId] = { ...updatedWidget };
  if (props.templateId) {
    saveLayout();
  }
};

const handleDeleteWidget = async (widgetId) => {
  if (!confirm('Are you sure you want to delete this widget?')) return;
  
  isSaving.value = true;
  
  try {
    // Get the widget before deleting it (we need the info for backend deletion)
    const widgetToDelete = widgets.value[widgetId];
    
    // If it's an events widget, delete the event from the backend
    if (widgetToDelete?.type === 'events') {
      try {
        const eventName = widgetToDelete.source || widgetToDelete.title?.replace(/\s+/g, '_');
        if (eventName) {
          const response = await Event.delete('', { dataID: props.dataID, name: eventName });
          if (response.status !== 'success') {
            console.error('Failed to delete event from backend:', response);
            // Continue with widget deletion even if backend deletion fails
          }
        }
      } catch (error) {
        console.error('Error deleting event from backend:', error);
        // Continue with widget deletion even if backend deletion fails
      }
    }
    
    // Remove from layout and widgets
    layout.value = layout.value.filter(item => item.i !== widgetId);
    
    // Create a new widgets object without the deleted widget
    // eslint-disable-next-line no-unused-vars
    const { [widgetId]: _removed, ...remainingWidgets } = widgets.value;
    widgets.value = remainingWidgets;
    
    // Save to Firestore if we have a templateId
    if (props.templateId) {
      const templateRef = doc(db, 'templates', props.templateId);
      const templateDoc = await getDoc(templateRef);
      
      if (templateDoc.exists()) {
        const templateData = templateDoc.data();
        const updatedPages = templateData.pages.map(p => 
          p.id === props.page.id 
            ? { ...p, layout: layout.value, widgets: { ...widgets.value } }
            : p
        );
        
        await updateDoc(templateRef, { pages: updatedPages });
        
        // Update the store with the new template data
        const updatedTemplateData = {
          ...templateData,
          pages: updatedPages,
          id: props.templateId,
          dataID: props.dataID
        };
        await store.dispatch('setTemplate', updatedTemplateData);
      }
    } else {
      console.error('Cannot save to Firestore: templateId is not available');
    }
    
    emit('deleteWidget', widgetId);
  } catch (error) {
    console.error('Error deleting widget:', error);
    alert('Failed to delete widget. Please try again.');
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div ref="gridRootRef" class="flex flex-col">
    <!-- Toolbar -->
    <div v-if="canEditDashboard" class="flex flex-wrap justify-between items-center gap-2 mb-4 p-3 rounded-lg">
      <div class="flex gap-2">
        <button
          v-if="editMode"
          @click="handleAddWidget"
          type="button"
          class="btn-primary flex items-center gap-2"
        >
          <PlusIcon class="w-4 h-4" />
          Add Widget
        </button>
      </div>
      
      <div class="flex items-center gap-3 flex-wrap">
        <div v-if="isSaving" class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <LoadingAnimation3 size="20px" />
          <span>Saving...</span>
        </div>
        
        <!-- Edit Mode Toggle Switch (admin or editDashboard permission only) -->
        <div v-if="canEditDashboard && showEditModeToggle" class="flex items-center gap-2">
          <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Edit Layout</span>
          <div class="flex items-center">
            <input
              id="editModeToggle"
              type="checkbox"
              v-model="editMode"
              class="toggle-checkbox hidden"
            />
            <label 
              for="editModeToggle" 
              class="toggle-label block w-12 h-6 rounded-full cursor-pointer relative transition-colors"
              :class="editMode ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'"
            >
              <span
                class="absolute left-0 top-0 w-6 h-6 bg-white border border-gray-300 rounded-full transition-transform duration-200"
                :class="editMode ? 'translate-x-6' : ''"
              ></span>
            </label>
          </div>
        </div>

      </div>
    </div>

    <!-- Empty State -->
    <div v-if="!hasWidgets" class="flex flex-col items-center justify-center h-full dark:bg-gray-800 rounded-lg p-12">
      <div class="text-center">
        <svg class="w-24 h-24 mx-auto text-gray-400 dark:text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z" />
        </svg>
        <h3 class="text-xl font-bold text-gray-700 dark:text-gray-300 mb-2">No Widgets Yet</h3>
        <div v-if="canEditDashboard && editMode">
          <p class="text-gray-600 dark:text-gray-400 mb-6">
            Start building your dashboard by adding widgets
          </p>
          <button
            @click="handleAddWidget"
            class="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold flex items-center gap-2 mx-auto transition-all shadow-lg"
          >
            <PlusIcon class="w-5 h-5" />
            Add Your First Widget
          </button>
        </div>
      </div>
    </div>

    <!-- Grid Layout -->
    <div v-else class="flex-grow min-h-[300px] sm:min-h-[600px]">
      <GridLayout
        v-model:layout="layout"
        :col-num="colNum"
        :row-height="30"
        :is-draggable="editMode && !isMobile"
        :is-resizable="editMode && !isMobile"
        :is-mirrored="false"
        :vertical-compact="isMobile"
        :prevent-collision="false"
        :margin="[10, 10]"
        :use-css-transforms="true"
        @layout-updated="handleLayoutUpdate"
      >
        <GridItem
          v-for="item in getDisplayLayout()"
          :key="item.i"
          :x="item.x"
          :y="item.y"
          :w="item.w"
          :h="item.h"
          :i="item.i"
          :min-w="isMobile ? 1 : (item.minW || 2)"
          :min-h="item.minH || 2"
          :max-w="isMobile ? 1 : (item.maxW || 12)"
          :max-h="item.maxH || 100"
          :static="isMobile"
          @move="handleItemMove"
          @moved="handleItemMoved"
          @resize="handleItemResize"
          @resized="handleItemResized"
          class="rounded-mc-sm overflow-hidden"
        >
          <div class="h-full relative">
            <!-- Widget Header (shown in edit mode) - Overlays content -->
            <div 
              v-if="editMode" 
              class="absolute top-0 left-0 right-0 z-10 px-3 py-2 flex justify-end"
            >
              <div class="flex gap-1 bg-mc-cloud dark:bg-mc-charcoal border border-mc-steel dark:border-line-on-dark rounded-mc-sm p-2">
                <!-- Only show edit button for non-events widgets -->
                <button
                  v-if="widgets[item.i]?.type !== 'events'"
                  @click.stop="handleEditWidget(item.i)"
                  class="p-1 hover:bg-blue-200 dark:hover:bg-blue-700 rounded transition-colors"
                  aria-label="Edit widget"
                  title="Edit widget"
                >
                  <PencilSquareIcon class="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </button>
                <button
                  @click.stop="handleDeleteWidget(item.i)"
                  class="p-1 hover:bg-red-200 dark:hover:bg-red-700 rounded transition-colors"
                  aria-label="Delete widget"
                  title="Delete widget"
                >
                  <svg class="w-4 h-4 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>

            <!-- Widget Content Area -->
            <div class="h-full overflow-auto">
              <WidgetRenderer
                v-if="widgets[item.i]"
                :key="`${item.i}-${widgets[item.i]._loading ? 'loading' : 'loaded'}`"
                :widget="widgets[item.i]"
                :widget-id="item.i"
                :dataID="dataID"
                :timeFormat="timeFormat"
                :streamData="streamData"
                :sourceErrors="sourceErrors"
                :historicalData="historicalData"
                :editMode="editMode"
                @update-widget="handleUpdateWidget"
              />
              <div v-else class="h-full flex items-center justify-center bg-mc-cloud dark:bg-mc-charcoal rounded-mc-sm border-2 border-dashed border-mc-steel dark:border-line-on-dark">
                <div class="text-center">
                  <p class="text-ink-muted dark:text-on-dark-muted font-body font-medium">No widget data</p>
                </div>
              </div>
            </div>
          </div>
        </GridItem>
      </GridLayout>
    </div>
  </div>
</template>

<style>
/* vue3-grid-layout styles */
.vue-grid-layout {
  background: transparent;
}

.vue-grid-item {
  transition: all 200ms ease;
}

.vue-grid-item.vue-grid-placeholder {
  background: rgba(59, 130, 246, 0.2);
  border: 2px dashed #3b82f6;
  border-radius: 0.5rem;
}

.vue-grid-item > .vue-resizable-handle {
  background-image: none;
  opacity: 0;
  transition: opacity 200ms;
}

.vue-grid-item:hover > .vue-resizable-handle {
  opacity: 1;
}

.vue-grid-item > .vue-resizable-handle::after {
  content: '';
  position: absolute;
  right: 3px;
  bottom: 3px;
  width: 8px;
  height: 8px;
  border-right: 2px solid #3b82f6;
  border-bottom: 2px solid #3b82f6;
}
</style>
